import { useMutation, useQueryClient } from '@tanstack/react-query';

import { downloadBlob } from '#lib/downloadBlob';
import { useErrorStore } from '#lib/stores/errorStore';

import {
	createCampaign,
	updateCampaign,
	deleteCampaign,
	confirmAndSend,
	abandonPayment,
	resumePayment,
	confirmWarnings,
	cancelCampaign,
	refreshCampaignStatus,
	syncCampaignLists,
	downloadSendPdf,
	exportCampaignToCsv,
} from '../../api/postMailingApi';

import { postMailingCampaignsKeys, postMailingCampaignsMutationKeys } from './keys';

export function useCreatePostMailingCampaign() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: postMailingCampaignsMutationKeys.create,
		mutationFn: createCampaign,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.list() });
		},
	});
}

export function useUpdatePostMailingCampaign() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: postMailingCampaignsMutationKeys.update,
		mutationFn: ({ id, updates }) => updateCampaign({ id, updates }),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.list() });
			if (data?.id) {
				queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.detail(data.id) });
			}
		},
	});
}

export function useDeletePostMailingCampaign() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: postMailingCampaignsMutationKeys.delete,
		mutationFn: deleteCampaign,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.list() });
		},
	});
}

export function useSyncPostMailingCampaignLists() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: postMailingCampaignsMutationKeys.syncLists,
		mutationFn: syncCampaignLists,
		onSuccess: (_data, { campaignId }) => {
			queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.detail(campaignId) });
			queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.list() });
			// Recipient count changed → the cached cost estimate is now stale.
			queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.costEstimate(campaignId) });
		},
	});
}

export function useConfirmAndSendPostMailing() {
	const queryClient = useQueryClient();
	const showSuccess = useErrorStore((s) => s.showSuccess);
	const showError = useErrorStore((s) => s.showError);

	return useMutation({
		mutationKey: postMailingCampaignsMutationKeys.confirmAndSend,
		mutationFn: confirmAndSend,
		onSuccess: (data, campaignId) => {
			// Paid path: redirect the browser to Stripe's hosted Checkout. The send is released by the backend
			// webhook after payment; we don't invalidate caches here because we're navigating away.
			if (data?.checkoutUrl) {
				window.location.href = data.checkoutUrl;
				return;
			}
			// Special-format path: no auto-send / no payment — the backend flagged the campaign for a manual
			// offer. Stay on the page, refresh so the 'awaiting_offer' status shows, and confirm to the user.
			if (data?.offerRequested) {
				queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.detail(campaignId) });
				queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.list() });
				showSuccess({
					title: 'Angebot angefragt',
					message: 'Wir prüfen Ihr Sonderformat und melden uns mit einem individuellen Angebot.',
				});
				return;
			}
			// Free path (owner org): no payment — the backend already released the send. Forward to the same
			// success page a paid send lands on (it invalidates caches and redirects to the campaign).
			if (data?.released) {
				window.location.href = `/post-mailing/checkout-success?campaignId=${campaignId}`;
				return;
			}
			// None of the known outcomes — without this the cost modal would just sit there silently.
			showError({
				title: 'Versand konnte nicht gestartet werden',
				message: 'Unerwartete Antwort vom Server. Bitte versuchen Sie es erneut.',
			});
		},
	});
}

export function useAbandonPostMailingPayment() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: postMailingCampaignsMutationKeys.abandonPayment,
		mutationFn: abandonPayment,
		onSuccess: (_, campaignId) => {
			queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.detail(campaignId) });
			queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.list() });
		},
	});
}

export function useResumePostMailingPayment() {
	const queryClient = useQueryClient();
	const showInfo = useErrorStore((s) => s.showInfo);

	return useMutation({
		mutationKey: postMailingCampaignsMutationKeys.resumePayment,
		mutationFn: resumePayment,
		onSuccess: (data, campaignId) => {
			// Session still open → straight back to the hosted Checkout, no cache work (navigating away).
			if (data?.checkoutUrl) {
				window.location.href = data.checkoutUrl;
				return;
			}
			queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.detail(campaignId) });
			queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.list() });
			if (data?.paymentInFlight) {
				showInfo({
					title: 'Zahlung wird verarbeitet',
					message:
						'Ihre Zahlung ist bereits eingegangen und wird gerade verarbeitet. Der Versand startet automatisch.',
					autoClose: true,
				});
				return;
			}
			if (data?.expired) {
				showInfo({
					title: 'Zahlungslink abgelaufen',
					message:
						'Der Zahlungslink war abgelaufen. Die Kampagne ist wieder ein Entwurf — starten Sie den Versand einfach erneut.',
					autoClose: true,
				});
			}
		},
	});
}

export function useConfirmPostMailingWarnings() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: postMailingCampaignsMutationKeys.confirmWarnings,
		mutationFn: confirmWarnings,
		onSuccess: (_data, { campaignId }) => {
			// Resolving warnings changes both per-send statuses and the campaign status.
			queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.detail(campaignId) });
			queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.sendsByCampaign(campaignId) });
			queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.sendsStats(campaignId) });
			queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.list() });
		},
	});
}

export function useCancelPostMailingCampaign() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: postMailingCampaignsMutationKeys.cancel,
		mutationFn: cancelCampaign,
		onSuccess: (_, campaignId) => {
			queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.detail(campaignId) });
			queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.list() });
		},
	});
}

export function useRefreshPostMailingStatus() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: postMailingCampaignsMutationKeys.refreshStatus,
		mutationFn: refreshCampaignStatus,
		onSuccess: (_, campaignId) => {
			queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.detail(campaignId) });
			queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.sendsByCampaign(campaignId) });
			queryClient.invalidateQueries({ queryKey: postMailingCampaignsKeys.sendsStats(campaignId) });
		},
	});
}

export function useDownloadPostMailingSendPdf() {
	return useMutation({
		mutationKey: postMailingCampaignsMutationKeys.downloadSendPdf,
		mutationFn: ({ campaignId, sendId }) => downloadSendPdf(campaignId, sendId),
		onSuccess: ({ blob, filename }) => {
			downloadBlob(blob, filename);
		},
	});
}

export function useExportPostMailingCsv() {
	return useMutation({
		mutationKey: postMailingCampaignsMutationKeys.exportCsv,
		mutationFn: ({ campaignId, options }) => exportCampaignToCsv(campaignId, options),
		onSuccess: ({ blob, filename }) => downloadBlob(blob, filename),
	});
}
